/**
 * useGeneratePdfFromBlocks Hook
 *
 * Generates PDF from UI blocks using the Blocks app's convert_to_pdf action.
 * Returns an S3 signed URL that can be used to download or fetch the PDF.
 */

import { useState, useCallback } from 'react';
import { executeAppAction, BASE_URL } from '@/services/DataService';
import type { UIBlock, ContainerBlock, InsightCardBlock } from '@/types/ui-blocks';

export interface GeneratePdfOptions {
  /** Array of UI blocks to convert to PDF */
  blocks: (UIBlock | ContainerBlock | InsightCardBlock)[];
  /** Title of the PDF document */
  title?: string;
  /** PDF generation options */
  options?: {
    format?: string;
  };
}

export interface GeneratePdfResult {
  /** S3 signed URL for downloading the PDF */
  url: string;
  /** S3 key where the PDF is stored */
  s3_key: string;
  /** Title of the document */
  title: string;
}

/** One item of the Blocks convert_to_pdf action's batch payload. */
export interface BlocksPdfPayload {
  type?: 'blocks' | 'html';
  blocks?: GeneratePdfOptions['blocks'];
  source?: string;
  title?: string;
  options?: {
    format?: string;
    viewport_width?: number;
  };
}

/**
 * Core of the `blocks/convert_to_pdf` call, shared by this hook and the
 * HTML-report export bridge: executes the action, validates the response
 * envelope, and returns the signed-URL payload. Throws on failure.
 */
export async function generatePdfWithBlocksApp(payload: BlocksPdfPayload): Promise<GeneratePdfResult> {
  const response = await executeAppAction(
    BASE_URL,
    'blocks',
    'convert_to_pdf',
    [],
    [payload]
  );

  // The execute_app route returns error-status results as HTTP 200 with the
  // Error serialized to {}, so the status flag is the only failure signal.
  if (response?.status === 'error') {
    throw new Error('PDF generation failed');
  }

  if (!response?.items?.[0]?.data) {
    throw new Error('Invalid response from PDF generation service');
  }

  const { url, s3_key, title: resultTitle } = response.items[0].data;

  if (!url) {
    throw new Error('No URL returned from PDF generation service');
  }

  return {
    url,
    s3_key,
    title: resultTitle || payload.title || 'Report'
  };
}

/**
 * Hook for generating PDFs from UI blocks
 */
export const useGeneratePdfFromBlocks = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const generatePdf = useCallback(async (options: GeneratePdfOptions): Promise<GeneratePdfResult> => {
    setIsGenerating(true);
    setError(null);

    try {
      const { blocks, title = 'Report', options: pdfOptions } = options;

      return await generatePdfWithBlocksApp({
        blocks,
        title,
        options: pdfOptions || { format: 'A4' }
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to generate PDF');
      setError(error);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  return {
    generatePdf,
    isGenerating,
    error
  };
};
